import { MIN_SUPPORTED_WIDTH } from '@toggle/design-system';
import { useEffect, useState } from 'react';

import {
  MediaQueriesColorSchemes,
  MediaQueriesOrientation,
} from '~/hooks/MediaQueryList';

export interface MediaQueryStoreValue<T> {
  currentMedia: T;
  initialize: () => void;
}

const listenMatchMedia = (
  m: MediaQueryList,
  listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void
) => {
  if (typeof m.addEventListener === 'function') {
    m.addEventListener('change', listener);
    return () => m.removeEventListener('change', listener);
  } else {
    m.addListener(listener);
    return () => m.removeListener(listener);
  }
};

export const useTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(() =>
    window ? window.innerWidth < MIN_SUPPORTED_WIDTH : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsTouchDevice(window.innerWidth < MIN_SUPPORTED_WIDTH);
    };

    if (window) {
      window.addEventListener('resize', handleResize);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isTouchDevice;
};

export function useMediaQueryColorScheme() {
  const darkModeMatchMedia = window.matchMedia?.(
    MediaQueriesColorSchemes.DARK_MODE
  );

  const [isDarkMode, setIsDarkMode] = useState(darkModeMatchMedia?.matches);

  const darkModeListener = ({ matches }: MediaQueryListEvent) => {
    setIsDarkMode(matches);
  };

  useEffect(() => {
    if (!darkModeMatchMedia) {
      return undefined;
    }

    return listenMatchMedia(darkModeMatchMedia, darkModeListener);
  }, []);

  return { isDarkMode };
}

const OrientationMatchMedias = Object.values(MediaQueriesOrientation).map(
  v => ({
    value: v,
    matchMedia: window.matchMedia(v),
  })
);

const getCurrentOrientation = () => {
  const orientationMatcher = OrientationMatchMedias.find(({ matchMedia }) => {
    return matchMedia.matches;
  });
  return orientationMatcher?.value;
};

export function useMediaQueryOrientation() {
  const [orientation, setOrientation] = useState(getCurrentOrientation);

  const updateOrientation = () => setOrientation(getCurrentOrientation);

  useEffect(() => {
    const unlisten = OrientationMatchMedias.map(({ matchMedia }) =>
      listenMatchMedia(matchMedia, updateOrientation)
    );

    return () => {
      unlisten.forEach(l => l());
    };
  }, []);

  return orientation;
}
